import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { EnvelopeSimple, Eye, EyeSlash, ArrowLeft, Lightning } from "@phosphor-icons/react";
import axios from "axios";
import { API, useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const EmailAuthModal = ({ onClose }) => {
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotSentMessage, setForgotSentMessage] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setForgotSentMessage("");
    if (!email) {
      setError("Enter the email on your FlashGrants account.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, {
        email,
        origin_url: window.location.origin,
      });
      setForgotSentMessage(
        res.data?.message ||
          "If an account with that email exists, we just sent a password-reset link. Check your inbox (and spam folder)."
      );
    } catch {
      // Server purposely returns 200 even on failure to prevent enumeration —
      // a real failure here would be a network drop. Show the same friendly message.
      setForgotSentMessage(
        "If an account with that email exists, we just sent a password-reset link. Check your inbox (and spam folder)."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (mode === "forgot") return handleForgotSubmit(e);
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login-email";

      // Attach first-touch UTM attribution captured by AnalyticsTracker so we
      // can later see which sources actually convert to signups.
      let utm = {};
      try {
        const stored = sessionStorage.getItem("fg_utm_attribution");
        if (stored) utm = JSON.parse(stored) || {};
      } catch { /* sessionStorage unavailable */ }

      const payload = mode === "register"
        ? { email, password, name, ...utm }
        : { email, password };

      const response = await axios.post(`${API}${endpoint}`, payload);

      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }

      setUser(response.data);

      // Check for pending promo code
      const pendingPromo = localStorage.getItem("pending_promo_code");
      if (pendingPromo) {
        try {
          await axios.post(`${API}/promo/redeem`, { code: pendingPromo });
          toast.success("Promo code redeemed!");
          localStorage.removeItem("pending_promo_code");
        } catch {
          // Promo failed silently, user can try later
        }
      }

      // Trial intent? Forward to Pricing where the auto-resume effect kicks in.
      // The Pricing page clears the flag after firing the Stripe checkout.
      let trialIntent = false;
      try { trialIntent = localStorage.getItem("pending_trial_start") === "1"; } catch { /* ignore */ }

      // Founding Member intent? Forward back to /founding where the auto-fire
      // effect kicks in and starts a Stripe checkout with the promo pre-applied.
      let foundingIntent = null;
      try { foundingIntent = localStorage.getItem("founding_pending_tier"); } catch { /* ignore */ }

      if (foundingIntent) {
        navigate("/founding?claim=1", { replace: true });
      } else if (trialIntent) {
        navigate("/pricing?start_trial=1", { replace: true });
      } else if (!response.data.nonprofit_profile?.org_name) {
        navigate("/profile", { replace: true, state: { setup: true } });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || JSON.stringify(e)).join(" "));
      } else if (!err.response) {
        // Network error — CORS blocked, no internet, or request didn't reach server
        setError("Unable to connect. Please check your internet connection and try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <Card
        className="w-full max-w-md bg-zinc-950 border-zinc-800 animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-6">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-500" />
            <span className="text-lg font-bold">FlashGrants</span>
          </div>

          <h2 className="text-2xl font-bold mb-1" data-testid="email-auth-title">
            {mode === "login" && "Welcome back"}
            {mode === "register" && "Create your account"}
            {mode === "forgot" && "Reset your password"}
          </h2>
          <p className="text-zinc-400 text-sm mb-6">
            {mode === "login" && "Sign in with your email and password"}
            {mode === "register" && "Get started with FlashGrants today"}
            {mode === "forgot" && "Enter your email and we'll send you a link to set a new password."}
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-sm mb-4" data-testid="email-auth-error">
              {error}
            </div>
          )}

          {mode === "forgot" && forgotSentMessage ? (
            <div
              className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm p-3 rounded-sm mb-4"
              data-testid="forgot-password-sent"
            >
              {forgotSentMessage}
              <p className="text-xs text-emerald-400/80 mt-2">
                Reset links expire after 30 minutes.
              </p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Full Name</label>
                <Input
                  type="text"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white h-11"
                  data-testid="register-name-input"
                  required
                />
              </div>
            )}

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Email</label>
              <Input
                type="email"
                placeholder="you@nonprofit.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-zinc-900 border-zinc-700 text-white h-11"
                data-testid="email-auth-email-input"
                required
              />
            </div>

            {mode !== "forgot" && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-zinc-400">Password</label>
                {mode === "login" && (
                  <button
                    type="button"
                    onClick={() => { setMode("forgot"); setError(""); setForgotSentMessage(""); }}
                    className="text-xs text-emerald-400 hover:text-emerald-300 font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={mode === "register" ? "Min. 6 characters" : "Your password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white h-11 pr-10"
                  data-testid="email-auth-password-input"
                  required
                  minLength={mode === "register" ? 6 : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm h-11"
              data-testid="email-auth-submit-btn"
            >
              {loading ? (
                <div className="loading-spinner mr-2" />
              ) : null}
              {mode === "login" && "Sign In"}
              {mode === "register" && "Create Account"}
              {mode === "forgot" && "Send reset link"}
            </Button>
          </form>
          )}

          <div className="mt-6 text-center">
            {mode === "forgot" ? (
              <button
                onClick={() => { setMode("login"); setError(""); setForgotSentMessage(""); }}
                className="text-sm text-emerald-400 hover:text-emerald-300 font-medium"
                data-testid="back-to-login-link"
              >
                ← Back to sign in
              </button>
            ) : (
              <p className="text-sm text-zinc-500">
                {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
                <button
                  onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                  className="text-emerald-400 hover:text-emerald-300 font-medium"
                  data-testid="email-auth-toggle-mode"
                >
                  {mode === "login" ? "Sign up" : "Sign in"}
                </button>
              </p>
            )}
          </div>

          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-zinc-500 hover:text-white p-1"
            data-testid="email-auth-close-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailAuthModal;
